import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core'
import { formatInrRupeeCompact } from '@core/helpers/format.helper'
import type {
    IShikshaPipelineRiskMetricView,
    IShikshaPipelineRiskStageView,
    ShikshaPipelineRiskStageKey,
} from '@core/models/shiksha-dashboard.model'
import {
    SHIKSHA_DASHBOARD_TEXT,
    SHIKSHA_PIPELINE_RISK_CONTENT,
    SHIKSHA_PIPELINE_RISK_DEFAULT_STUCK_DAYS,
    SHIKSHA_PIPELINE_RISK_RISK_KEYS,
    SHIKSHA_PIPELINE_RISK_RUPEE_COMPACT_FORMAT,
    SHIKSHA_PIPELINE_RISK_STAGE_DISPLAY_CODES,
    SHIKSHA_PIPELINE_RISK_STAGE_ORDER,
    SHIKSHA_PIPELINE_RISK_STAGE_LABELS,
    SHIKSHA_PIPELINE_RISK_UI,
} from '../constants/shiksha-dashboard.constants'
import { ShikshaDashboardSubtitleHeaderComponent } from '../shiksha-dashboard-subtitle-header/shiksha-dashboard-subtitle-header.component'
import { createEmptyPipelineRiskMetric } from '../utils/shiksha-pipeline-risk.util'

@Component({
    selector: 'app-shiksha-pipeline-risk',
    standalone: true,
    templateUrl: './shiksha-pipeline-risk.component.html',
    styleUrl: './shiksha-pipeline-risk.component.scss',
    imports: [ShikshaDashboardSubtitleHeaderComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShikshaPipelineRiskComponent {
    readonly content = SHIKSHA_PIPELINE_RISK_CONTENT
    readonly uiConfig = SHIKSHA_PIPELINE_RISK_UI
    readonly rupeeSymbol = SHIKSHA_DASHBOARD_TEXT.currencySymbolRupee
    readonly stages = input<readonly IShikshaPipelineRiskStageView[]>([])
    readonly renderedStages = computed<readonly IShikshaPipelineRiskStageView[]>(() => {
        const stages = this.stages()
        if (stages.length > 0) {
            return stages
        }
        return SHIKSHA_PIPELINE_RISK_STAGE_ORDER.map((stageKey) =>
            this.createFallbackStage(stageKey, SHIKSHA_PIPELINE_RISK_STAGE_LABELS[stageKey]),
        )
    })

    stageRiskAriaLabel(stage: IShikshaPipelineRiskStageView): string {
        const highRiskMetric = this.highRiskMetric(stage)
        const count = highRiskMetric.leadCount
        const countLabel = this.dealLabel(count)
        const amountSuffix =
            highRiskMetric.amount > 0
                ? this.content.stageRiskAriaAmountSuffix
                      .replace('{amount}', this.formatRupeeCompact(highRiskMetric.amount))
                      .replace('{atRiskSuffix}', this.content.atRiskSuffix)
                : ''
        return this.content.stageRiskAriaTemplate
            .replace('{stageLabel}', stage.stageLabel)
            .replace('{highRiskLabel}', this.content.highRiskLabel)
            .replace('{count}', String(count))
            .replace('{dealLabel}', countLabel)
            .replace('{amountSuffix}', amountSuffix)
    }

    totalStageLeads(stage: IShikshaPipelineRiskStageView): number {
        return this.highRiskMetric(stage).leadCount
    }

    dealLabel(count: number): string {
        return count <= 1 ? this.content.dealLabelSingular : this.content.dealLabelPlural
    }

    showAtRiskAmount(stage: IShikshaPipelineRiskStageView): boolean {
        return this.highRiskMetric(stage).amount > 0
    }

    private totalStageAmountRupeeLabel(stage: IShikshaPipelineRiskStageView): string {
        return this.formatRupeeCompact(this.highRiskMetric(stage).amount)
    }

    /** Compact INR string without the leading ₹ (for split typography). */
    highRiskAmountFigures(stage: IShikshaPipelineRiskStageView): string {
        const full = this.totalStageAmountRupeeLabel(stage)
        return full.startsWith(this.rupeeSymbol) ? full.slice(this.rupeeSymbol.length) : full
    }

    stageSummaryTitle(stage: IShikshaPipelineRiskStageView): string {
        const stageCode = SHIKSHA_PIPELINE_RISK_STAGE_DISPLAY_CODES[stage.stageKey]
        const stuckDays =
            stage.stuckAfterDays ?? SHIKSHA_PIPELINE_RISK_DEFAULT_STUCK_DAYS[stage.stageKey]
        return this.content.stageSummaryTitleTemplate
            .replace('{stage}', stageCode)
            .replace('{days}', `${stuckDays}`)
    }

    private highRiskMetric(stage: IShikshaPipelineRiskStageView): IShikshaPipelineRiskMetricView {
        return (
            stage.metrics.find(
                (metric) => metric.riskKey === SHIKSHA_PIPELINE_RISK_RISK_KEYS.HIGH,
            ) ?? {
                ...createEmptyPipelineRiskMetric({
                    riskKey: SHIKSHA_PIPELINE_RISK_RISK_KEYS.HIGH,
                    riskLabel: this.uiConfig.highLabel,
                }),
            }
        )
    }

    private formatRupeeCompact(amount: number): string {
        return formatInrRupeeCompact(amount, SHIKSHA_PIPELINE_RISK_RUPEE_COMPACT_FORMAT)
    }

    private createFallbackStage(
        stageKey: ShikshaPipelineRiskStageKey,
        stageLabel: string,
    ): IShikshaPipelineRiskStageView {
        return {
            stageKey,
            stageLabel,
            metrics: [
                createEmptyPipelineRiskMetric({
                    riskKey: SHIKSHA_PIPELINE_RISK_RISK_KEYS.HIGH,
                    riskLabel: this.uiConfig.highLabel,
                }),
                createEmptyPipelineRiskMetric({
                    riskKey: SHIKSHA_PIPELINE_RISK_RISK_KEYS.MODERATE,
                    riskLabel: this.uiConfig.moderateLabel,
                }),
            ],
        }
    }
}
